import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../store/authContext';
import { 
  Home,
  Package,
  TrendingUp,
  DollarSign,
  Users,
  User,
  Settings,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  BarChart2,
  UserCircle,
  AlertCircle
} from 'lucide-react';

const Sidebar = ({ collapsed, onToggleSidebar }) => {
  const location = useLocation();
  const { currentUser, hasPermission, logout } = useContext(AuthContext);
  
  // Verificar se o link está ativo
  const isActive = (path) => location.pathname === path;
  
  // Lidar com o logout
  const handleLogout = () => {
    logout();
  };

  // Definir ícones e rótulos para os itens de navegação
  const mainNavItems = [
    { path: '/', label: 'Dashboard', icon: <Home size={20} />, permission: true },
    { path: '/produtos', label: 'Produtos', icon: <Package size={20} />, permission: hasPermission('view_products') },
    { path: '/trafego-marketing', label: 'Tráfego Pago', icon: <TrendingUp size={20} />, permission: hasPermission('view_traffic') },
    { path: '/financas', label: 'Finanças', icon: <DollarSign size={20} />, permission: hasPermission('view_finances') },
    { path: '/colaboradores', label: 'Colaboradores', icon: <Users size={20} />, permission: hasPermission('view_team') },
    { path: '/usuarios', label: 'Usuários', icon: <User size={20} />, permission: hasPermission('edit_team') }
  ];

  // Itens de navegação complementares (sem restrição de permissão)
  const systemNavItems = [
    { path: '/profile', label: 'Meu Perfil', icon: <UserCircle size={20} />, permission: true },
    { path: '/settings', label: 'Configurações', icon: <Settings size={20} />, permission: true },
    { path: '/help', label: 'Ajuda', icon: <HelpCircle size={20} />, permission: true }
  ];
  
  return (
    <aside className={`bg-white h-screen border-r border-gray-200 flex flex-col z-20 shadow-sm transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}>
      {/* Logo e Título */}
      <div className={`h-16 border-b border-gray-200 flex items-center px-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        <div className="text-xl font-bold text-blue-600 flex items-center">
          <DollarSign className="h-6 w-6" />
          {!collapsed && <span className="ml-2">Financeiro</span>}
        </div>
        <button 
          className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
          onClick={onToggleSidebar}
        >
          {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>
      
      {/* Menu de Navegação Principal */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className={`px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 ${collapsed ? 'text-center' : ''}`}>
          {!collapsed && 'Principal'}
        </div>
        <ul className="space-y-1 px-3 mb-6">
          {mainNavItems.map((item, index) => 
            item.permission && (
              <li key={index}>
                <Link 
                  to={item.path} 
                  className={`flex items-center ${collapsed ? 'justify-center' : ''} py-2 px-3 rounded-lg ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  } transition-colors`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!collapsed && <span className="ml-3 font-medium">{item.label}</span>}
                </Link>
              </li>
            )
          )}
        </ul>

        {/* Menu de Navegação do Sistema */}
        <div className={`px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 ${collapsed ? 'text-center' : ''}`}>
          {!collapsed && 'Sistema'}
        </div>
        <ul className="space-y-1 px-3">
          {systemNavItems.map((item, index) => 
            item.permission && (
              <li key={`system-${index}`}>
                <Link 
                  to={item.path} 
                  className={`flex items-center ${collapsed ? 'justify-center' : ''} py-2 px-3 rounded-lg ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  } transition-colors`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!collapsed && <span className="ml-3 font-medium">{item.label}</span>}
                </Link>
              </li>
            )
          )}
        </ul>
      </nav>
      
      {/* Rodapé com Informações do Usuário */}
      <div className="border-t border-gray-200 p-4">
        <div className={`flex ${collapsed ? 'flex-col items-center' : 'items-center'} mb-4`}>
          <div className="h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
            {currentUser?.name?.charAt(0) || 'U'}
          </div>
          
          {!collapsed && (
            <div className="ml-3 overflow-hidden">
              <div className="text-sm font-medium text-gray-800 truncate">{currentUser?.name}</div>
              <div className="text-xs text-gray-500 truncate">
                {currentUser?.role === 'admin' ? 'Administrador' : 
                 currentUser?.role === 'manager' ? 'Gerente' : 
                 currentUser?.role === 'marketing' ? 'Marketing' : 'Usuário'}
              </div>
            </div>
          )}
        </div>
        
        <button 
          onClick={handleLogout}
          className={`flex ${collapsed ? 'justify-center' : ''} items-center w-full py-2 px-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors`}
          aria-label="Sair do sistema"
        >
          <LogOut size={20} />
          {!collapsed && <span className="ml-3 font-medium">Sair</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
